import { Box, Grid, GridItem, Text, Icon, Badge, useDisclosure } from '@chakra-ui/react'
import { FiCpu, FiPackage } from 'react-icons/fi'
import { GoDotFill } from 'react-icons/go'
import { toast } from 'react-toastify'
import { PhysicalDeviceModal } from './modals/PhysicalDeviceModal'
import { DeviceLite } from '../../api/types'
import { useDeleteDevicesMutation } from '../../api/api'
import { KebabMenu } from '../utils'
import React, { useState } from 'react'
import { DeleteDeviceModal } from './modals/DeleteDeviceModal'

interface Props {
  device: DeviceLite
  isLast?: boolean
}

export default function DeviceListItem({ device, isLast }: Props) {
  const editDisc = useDisclosure()
  const confirmDisc = useDisclosure()

  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteDevices] = useDeleteDevicesMutation()

  const handleDelete = async () => {
    setIsDeleting(true)
    const toastId = toast.info('Deleting device...', {
      style: { padding: '10px 20px' },
      autoClose: false,
      isLoading: true,
    })
    try {
      await (deleteDevices(device.id) as any).unwrap?.()
      toast.update(toastId, {
        render: 'Successfully deleted',
        type: 'success',
        isLoading: false,
        autoClose: 2500,
      })
      confirmDisc.onClose()
    } catch (e: any) {
      toast.update(toastId, {
        render: e?.data?.detail || 'Failed to delete device!',
        type: 'error',
        isLoading: false,
        autoClose: 3500,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Grid
        templateColumns="3fr 2fr 2fr 1fr 1fr 250px"
        px={4}
        py={3}
        borderBottom={isLast ? 'none' : '1px solid'}
        borderColor="gray.100"
        alignItems="center"
        _hover={{ bg: 'gray.50' }}
      >
        <GridItem>
          <Box display="flex" alignItems="center" gap={4}>
            <Box
              border="1px solid"
              borderColor="gray.300"
              borderRadius="md"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              p={2}
            >
              {device.is_virtual ? <FiCpu /> : <FiPackage />}
            </Box>
            <Box>
              <Text fontFamily="Inter" fontWeight={400} fontSize="12px" color="#484848">
                {device.title}
              </Text>
              <Badge
                mt={1}
                bg={device.is_virtual ? 'blue.50' : 'gray.100'}
                color={device.is_virtual ? 'blue.400' : 'gray.600'}
                fontSize="11px"
                fontWeight="400"
              >
                {device.is_virtual ? 'Virtual Device' : 'Physical Device'}
              </Badge>
            </Box>
          </Box>
        </GridItem>

         <GridItem>
          <Text fontFamily="Inter" fontWeight={400} fontSize="12px" color="#484848">
            {device?.physical_id || ''}
          </Text>
        </GridItem>

        <GridItem>
          <Text fontFamily="Inter" fontWeight={400} fontSize="12px" color="#484848">
            {device.location?.title || 'No location'}
          </Text>
        </GridItem>

        <GridItem>
          <Text fontFamily="Inter" fontWeight={400} fontSize="12px" color="#484848">
            {device.location?.department.name || 'No department'}
          </Text>
        </GridItem>

        <GridItem>
          <Icon
            as={GoDotFill}
            boxSize="24px"
            color={device.has_data ? '#00d183' : 'red'}
          />
        </GridItem>

        <GridItem textAlign="right">
          <KebabMenu onEdit={editDisc.onOpen} onRemove={confirmDisc.onOpen} />
        </GridItem>
      </Grid>

      <PhysicalDeviceModal
        key={device.id}
        isOpen={editDisc.isOpen}
        onClose={editDisc.onClose}
        editDevice={device}
      />

      <DeleteDeviceModal
        isOpen={confirmDisc.isOpen}
        onClose={isDeleting ? () => {} : confirmDisc.onClose}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title="Remove device"
        body={`Are you sure you want to remove “${device.title}”? This action cannot be undone.`}
        confirmText="Remove"
      />
    </>
  )
}
